import React, {useEffect, useState} from 'react';
import {LoginModal} from './components/auth/LoginModal';
import {useAuth} from './hooks/useAuth';
import {AuthCallback} from './components/auth/AuthCallback';
import {AccountLinkModal} from './components/auth/AccountLinkModal';
import {AgeVerificationModal} from './components/auth/AgeVerificationModal';
import {gameService} from './services/gameService';
import {useGameStore} from './stores/gameStore';
import {useLobbyStore} from './stores/lobbyStore';
import {GameStateDTO} from "@hexcrawl/shared-utils";
import {launchCombatScene} from './services/combatService';
import {LobbyScreen} from './components/lobby/LobbyScreen';
import {LobbyAlert} from './components/LobbyAlert';
import {AccountWidget} from './components/auth/AccountWidget';
import {BugReportButton} from './components/BugReportButton';
import {CombatComponent} from './components/combat/CombatComponent';
import {CombatOutcomeOverlay} from './components/combat/CombatOutcomeOverlay';
import {LoadingOverlay} from './components/common/LoadingOverlay';
import {CheatToggle} from './components/common/CheatToggle';
import {MapHUD} from './components/map/MapHUD';
import {MapTooltip} from './components/map/MapTooltip';
import {GameOverOverlay} from './components/map/GameOverOverlay';
import {EncounterOverlay} from './components/encounter/EncounterOverlay';
import {LootOverlay} from './components/inventory/LootOverlay';
import {useLootStore} from './stores/lootStore';
import {useDiscoveryStore} from './stores/discoveryStore';
import {npcService} from './services/npcService';
import {useNpcStore} from './stores/npcStore';
import './App.css';
import {LoadingKeys, useUiStore} from './stores/uiStore';

export const App: React.FC = () => {
  const [showLink, setShowLink] = useState(false);
  const [lobbyAlertDismissed, setLobbyAlertDismissed] = useState(false);
  const [isTransitioningToGame, setIsTransitioningToGame] = useState(false);
  const {isAuthenticated, refreshUser, user} = useAuth();
  const isLoading = useUiStore((s) => s.isLoading);
  const loadingMessage = useUiStore((s) => s.loadingMessage);
  const startLoading = useUiStore((s) => s.startLoading);
  const stopLoading = useUiStore((s) => s.stopLoading);
  const errorMessage = useUiStore((s) => s.errorMessage);
  const clearUiError = useUiStore((s) => s.clearError);

  const gameState = useGameStore((s) => s.gameState);

  // Detect OAuth/email callback route
  const isCallbackRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth/callback');

  // Force re-render on URL/history changes triggered by AuthCallback
  const [_, setRouteVersion] = useState(0);
  useEffect(() => {
    const bump = () => setRouteVersion((v) => v + 1);
    window.addEventListener('popstate', bump);
    window.addEventListener('auth-callback-complete', bump);
    return () => {
      window.removeEventListener('popstate', bump);
      window.removeEventListener('auth-callback-complete', bump);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
  }, [isAuthenticated, refreshUser]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Logged out: stop active scenes, clear state
      const game = window.phaserGame;
      try {
        if (game && game.scene) {
          try { game.scene.stop('MapScene'); } catch {}
          try { game.scene.stop('CombatScene'); } catch {}
          game.scene.start('SplashScene');
        }
      } catch (e) {
        console.warn('Unable to switch to SplashScene on logout', e);
      }
      try { useGameStore.getState().clear(); } catch {}
      try { useLobbyStore.getState().clearRoster(); } catch {}
    }
  }, [isAuthenticated]);

  const startGame = async (gameState: GameStateDTO) => {
    const game = window.phaserGame;
    if (game && game.scene) {
      if (gameState.activeCombat) {
        await launchCombatScene(gameState);
      } else {
        game.scene.start('MapScene', gameState);
      }
    } else {
      console.warn('Phaser game not ready to start scene');
    }
  };

  const handleCreateMap = async (gameId: string) => {
    startLoading(LoadingKeys.ADVENTURE_TRANSITION, 'Creating map...');
    try {
      const gameState: GameStateDTO = await gameService.createMap({gameId});
      if (gameState) {
        useGameStore.getState().setGameState(gameState);
        if (gameState.id) {
          void useDiscoveryStore.getState().fetchDiscoveries(gameState.id);
        }
        if (gameState.map) {
          // Keep the transition key held — MapScene.onLoadComplete releases it
          // once the map is fully rendered and ready.
          await startGame(gameState);
        }
      }
    } catch (e) {
      stopLoading(LoadingKeys.ADVENTURE_TRANSITION);
      console.error('Failed to create game map:', e);
      alert('Failed to create game map. Is the server running on http://localhost:3000?');
    }
  };

  // Sync discoveries whenever the active game changes.
  // This fires on every game load path (new game, continue, resume after browser refresh).
  useEffect(() => {
    const gameId = gameState?.id;
    if (!gameId) return;
    void useDiscoveryStore.getState().fetchDiscoveries(gameId);
  }, [gameState?.id]);

  // Populate npcStore with hired NPCs on load/resume.
  useEffect(() => {
    const hiredNpcIds = gameState?.hiredNpcIds ?? [];
    if (!hiredNpcIds.length) return;
    Promise.all(hiredNpcIds.map((id) => npcService.getNpcById(id)))
      .then((npcs) => useNpcStore.getState().setNpcs(npcs))
      .catch(console.error);
  }, [gameState?.id]);

  // Load game config once on auth
  useEffect(() => {
    if (!isAuthenticated) return;
    gameService.getConfig().then((cfg) => {
      if (cfg) useGameStore.getState().setGameConfig(cfg);
    }).catch(console.error);
  }, [isAuthenticated]);

  // Detect when character creation completes (map not yet created) and trigger map creation.
  // isLoading is intentionally excluded from deps — it changes inside handleCreateMap itself,
  // which would cause a re-trigger loop. The !gameState.map guard prevents double-calls.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!gameState) return;
    if (!gameState.map && gameService.isCharacterReady(gameState)) {
      handleCreateMap(gameState.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, gameState]);

  const handleAdventureStarting = (message: string) => {
    setIsTransitioningToGame(true);
    startLoading(LoadingKeys.ADVENTURE_TRANSITION, message);
  };

  const handleAdventureError = () => {
    setIsTransitioningToGame(false);
    stopLoading(LoadingKeys.ADVENTURE_TRANSITION);
  };

  // When a game is loaded from the lobby, set it in the store and start the scene
  const handleGameLoaded = async (loadedGameState: GameStateDTO) => {
    useGameStore.getState().setGameState(loadedGameState);
    // Restore any pending loot that was awaiting claim when the session ended
    if (loadedGameState.pendingLoot) {
      useLootStore.getState().setPendingLoot(loadedGameState.pendingLoot);
    }
    if (loadedGameState.map) {
      if (loadedGameState.activeCombat) {
        // Combat scene doesn't have an onLoadComplete; release the transition before handing off.
        stopLoading(LoadingKeys.ADVENTURE_TRANSITION);
        await startGame(loadedGameState);
      } else {
        // Keep the transition key held — MapScene.onLoadComplete releases it once the map is ready.
        startLoading(LoadingKeys.ADVENTURE_TRANSITION, 'Loading map...');
        await startGame(loadedGameState);
      }
    }
    // If no map yet, the useEffect above will trigger map creation
  };

  const handleReturnToLobby = () => {
    const game = window.phaserGame;
    try {
      if (game && game.scene) {
        try { game.scene.stop('MapScene'); } catch {}
        try { game.scene.stop('CombatScene'); } catch {}
        game.scene.start('SplashScene');
      }
    } catch (e) {
      console.warn('Unable to switch to SplashScene', e);
    }
    useGameStore.getState().clear();
    useLobbyStore.getState().fetchRoster();
  };

  // Show lobby when authenticated, no active game, and not transitioning into one
  // isTransitioningToGame is separate from isLoading so CharacterCreation's own
  // loading keys don't accidentally unmount the lobby mid-creation.
  const showLobby = isAuthenticated && !gameState && !isTransitioningToGame;

  // Reset alert each time the lobby appears
  useEffect(() => {
    if (showLobby) setLobbyAlertDismissed(false);
  }, [showLobby]);

  return (
    <>
      {isCallbackRoute && <AuthCallback/>}

      {/* Lobby alert — must be dismissed before lobby */}
      {showLobby && !lobbyAlertDismissed && <LobbyAlert onDismiss={() => setLobbyAlertDismissed(true)}/>}

      {/* Lobby — shown when logged in with no active game */}
      {showLobby && lobbyAlertDismissed && <LobbyScreen onGameLoaded={handleGameLoaded} onAdventureStarting={handleAdventureStarting} onAdventureError={handleAdventureError} onManageEmail={() => setShowLink(true)}/>}

      {/* Loot overlay */}
      {isAuthenticated && gameState?.map && <LootOverlay/>}

      {/* Encounter overlay */}
      {isAuthenticated && gameState?.map && !gameState?.activeCombat && <EncounterOverlay/>}

      {/* Map HUD */}
      {isAuthenticated && gameState?.map && !gameState?.activeCombat && !isLoading && gameService.isCharacterReady(gameState) && gameState?.status !== 'ended' && <MapHUD/>}

      {/* Game Over overlay */}
      {isAuthenticated && gameState?.status === 'ended' && <GameOverOverlay onReturnToLobby={handleReturnToLobby}/>}

      {/* Combat UI */}
      {isAuthenticated && gameState?.activeCombat && <CombatComponent/>}
      {isAuthenticated && gameState?.activeCombat && <CombatOutcomeOverlay onReturnToLobby={handleReturnToLobby}/>}

      {/* Account widget — always on top when authenticated */}
      <AccountWidget onManageEmail={() => setShowLink(true)}/>
      <BugReportButton/>

      {/* Cheat toggle — only meaningful in-game, and reachable from both map and combat */}
      {isAuthenticated && gameState?.map && <CheatToggle/>}

      <MapTooltip/>

      {/* Login UI */}
      <LoginModal isOpen={!isAuthenticated}/>

      {/* Age verification — shown for new OAuth users who haven't attested yet */}
      <AgeVerificationModal isOpen={isAuthenticated && user?.hasAgeVerification === false} />

      {/* Account Link Modal */}
      <AccountLinkModal
        isOpen={showLink && Boolean(isAuthenticated)}
        onClose={() => setShowLink(false)}
      />

      {/* Blocking loading overlay */}
      {isLoading && <LoadingOverlay message={loadingMessage}/>}

      {/* Global error overlay */}
      {errorMessage && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100001, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <div style={{minWidth: 300, maxWidth: 600, background: '#2a1e1e', color: '#ffd7d7', border: '1px solid #b44', borderRadius: 8, padding: 16, boxShadow: '0 6px 24px rgba(0,0,0,0.6)'}}>
            <div style={{fontWeight: 'bold', marginBottom: 8}}>Something went wrong</div>
            <div style={{marginBottom: 12, whiteSpace: 'pre-wrap'}}>{errorMessage}</div>
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <button onClick={() => clearUiError()} style={{padding: '6px 10px', borderRadius: 4, background: '#b44', color: 'white', border: 'none', cursor: 'pointer'}}>Dismiss</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
